import React, { useState } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  Lock,
  Phone,
  Eye,
  EyeOff,
  ArrowRight,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  Smartphone,
  ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { clientLogin } from '../lib/clientAuthService';
import { apiFetch as fetch } from '../lib/apiConfig';
import { PairUpLogo } from '../components/common/PairUpLogo';
import { LanguageSwitcher } from '../components/common/LanguageSwitcher';
import { useLanguage } from '../i18n/LanguageContext';

export const ClientLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();

  const isCompanionInitial = typeof window !== 'undefined' && (
    window.location.pathname.includes('/companion/login') ||
    window.location.pathname.includes('/companion-login')
  );

  const [activeRole, setActiveRole] = useState<'client' | 'companion'>(isCompanionInitial ? 'companion' : 'client');
  const [loginMethod, setLoginMethod] = useState<'password' | 'otp'>('password');

  // Mobile + Password form
  const [loginMobile, setLoginMobile] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Forgot Password via APITXT SMS OTP state
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [resetMobile, setResetMobile] = useState('');
  const [resetVerificationId, setResetVerificationId] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [isResetOtpSent, setIsResetOtpSent] = useState(false);
  const [resetCooldown, setResetCooldown] = useState(0);

  // Mobile form
  const [mobileNumber, setMobileNumber] = useState('');
  const [mobileOtp, setMobileOtp] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otpVerificationId, setOtpVerificationId] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const handlePhonePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);

    const cleanPhone = loginMobile.replace(/\D/g, '').slice(-10);
    if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
      setErrorMsg('Please enter a valid 10-digit Indian mobile number (e.g. 9876543210).');
      return;
    }
    if (!password) {
      setErrorMsg('Please enter your password.');
      return;
    }

    setIsLoading(true);

    try {
      const { profile } = await clientLogin(cleanPhone, password, activeRole);

      login(profile.displayName, activeRole, {
        ...profile,
        name: profile.displayName,
        role: activeRole === 'companion' ? 'Companion Host & Member' : 'Verified Client',
      });

      setSuccessMsg(`Welcome back, ${profile.displayName}! Redirecting...`);

      setTimeout(() => {
        if (activeRole === 'companion') {
          navigate('/companion/dashboard', { replace: true });
        } else {
          navigate('/home', { replace: true });
        }
      }, 500);
    } catch (err: any) {
      let msg = err.message || 'Login failed. Please check your credentials.';
      if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
        msg = 'Invalid mobile number or password. Please verify your details.';
      } else if (err.code === 'auth/wrong-password') {
        msg = 'Incorrect password. Please try again or reset password.';
      } else if (err.code === 'auth/too-many-requests') {
        msg = 'Access temporarily disabled due to many failed attempts. Try again later.';
      }
      setErrorMsg(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMobileOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanPhone = mobileNumber.replace(/\D/g, '');
    if (!cleanPhone || cleanPhone.length < 10) {
      setErrorMsg('Please enter a valid 10-digit mobile number.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch('/api/v1/auth/otp/send', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: cleanPhone, role: activeRole }),
      });
      const data = await response.json();
      if (!response.ok || !data.success || typeof data.verificationId !== 'string') throw new Error(data.error?.message || 'Could not send OTP.');
      setOtpVerificationId(data.verificationId);
      setIsOtpSent(true);
      setSuccessMsg('OTP request accepted. Please check your SMS.');
    } catch (error: any) {
      setErrorMsg(error.message || 'OTP service is unavailable.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyMobileOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(mobileOtp)) {
      setErrorMsg('Enter the six-digit code sent to your mobile.');
      return;
    }
    const cleanPhone = mobileNumber.replace(/\D/g, '');
    setIsLoading(true);
    try {
      const response = await fetch('/api/v1/auth/otp/verify', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: cleanPhone, verificationId: otpVerificationId, otp: mobileOtp, role: activeRole }),
      });
      const data = await response.json();
      if (!response.ok || !data.success || !data.user || !data.customToken) {
        throw new Error(data.error?.message || 'OTP verification failed. Request a new code.');
      }
      await signInWithCustomToken(auth, data.customToken);
      const profile = data.user;
      const resolvedRole: 'client' | 'companion' =
        profile.role === 'companion' || profile.accountType === 'companion' ? 'companion' : 'client';
      const displayName =
        profile.fullName || profile.displayName || profile.name || `User ${cleanPhone.slice(-4)}`;
      login(displayName, resolvedRole, {
        ...profile,
        name: profile.fullName || profile.name || displayName,
        displayName,
        role: resolvedRole === 'companion' ? 'Companion Host & Member' : 'Verified Client',
        accountType: resolvedRole,
        companion_profile_status: profile.companion_profile_status || 'Inactive',
        membership_status: profile.membership_status || 'PENDING',
        payment_status: profile.payment_status || 'Pending',
        payment_verified: profile.payment_verified === true,
      });
      navigate(resolvedRole === 'companion' ? '/companion/dashboard' : '/home', { replace: true });
    } catch (error: any) {
      setErrorMsg(error.message || 'Could not verify OTP. Check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendResetOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);
    const cleanPhone = resetMobile.replace(/\D/g, '').slice(-10);
    if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
      setErrorMsg('Please enter your registered 10-digit Indian mobile number.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch('/api/v1/auth/password-reset/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: cleanPhone }),
      });
      const data = await response.json();
      if (!response.ok || !data.success || !data.verificationId) {
        throw new Error(data.error?.message || data.message || 'Could not send password reset OTP.');
      }
      setResetVerificationId(data.verificationId);
      setIsResetOtpSent(true);
      setResetCooldown(data.cooldownSeconds || 60);
      setSuccessMsg(data.message || 'Password reset OTP sent via SMS.');
    } catch (err: any) {
      setErrorMsg(err.message || 'Password reset OTP service is temporarily unavailable.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmPasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);
    const cleanPhone = resetMobile.replace(/\D/g, '').slice(-10);
    const cleanOtp = resetOtp.replace(/\D/g, '').slice(0, 6);
    if (cleanOtp.length !== 6) {
      setErrorMsg('Please enter the 6-digit SMS OTP code.');
      return;
    }
    if (!resetNewPassword || resetNewPassword.length < 8) {
      setErrorMsg('New password must be at least 8 characters and include letters and numbers.');
      return;
    }
    if (resetNewPassword !== resetConfirmPassword) {
      setErrorMsg('New password and confirmation do not match.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch('/api/v1/auth/password-reset/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile: cleanPhone,
          verificationId: resetVerificationId,
          otp: cleanOtp,
          newPassword: resetNewPassword,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error?.message || data.message || 'Failed to reset password.');
      }
      setSuccessMsg(data.message || 'Password reset successfully! You can now sign in with your new password.');
      setForgotPasswordMode(false);
      setIsResetOtpSent(false);
      setResetOtp('');
      setResetNewPassword('');
      setResetConfirmPassword('');
      setLoginMobile(cleanPhone);
    } catch (err: any) {
      setErrorMsg(err.message || 'Could not complete password reset.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col justify-between selection:bg-pink-500 selection:text-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <PairUpLogo className="w-10 h-10 shrink-0" />
          <div className="flex flex-col">
            <span className="text-xl font-black tracking-tight text-slate-900 leading-none">
              PairUp<span className="text-pink-600">Today</span>
            </span>
            <span className="text-[10px] font-semibold text-slate-400">Secure Member Login</span>
          </div>
        </Link>

        <div className="flex items-center gap-2 text-xs font-bold">
          <LanguageSwitcher variant="compact" />
          <span className="text-slate-500 hidden sm:inline">New client?</span>
          <Link
            to="/signup"
            className="px-3.5 py-1.5 bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white rounded-full transition-all shadow-xs"
          >
            {t('nav.signup')}
          </Link>
        </div>
      </header>

      {/* Main Form */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-6 my-6">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-xl max-w-md w-full p-6 sm:p-8 space-y-5">
          {/* Prominent Centered Golden Ring Logo */}
          <div className="flex justify-center mb-1">
            <PairUpLogo className="w-32 h-32" />
          </div>

          <div className="text-center space-y-1">
            <span className="text-[11px] font-black uppercase tracking-wider text-pink-600 block">
              Welcome Back
            </span>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight uppercase">
              Sign In
            </h1>
            <p className="text-xs text-slate-500">
              Access your Client Dashboard, bookings, and nearby companions.
            </p>
          </div>

          {/* Role Switcher */}
          <div className="bg-slate-100 p-1.5 rounded-2xl flex items-center gap-1 border border-slate-200">
            <button
              type="button"
              onClick={() => setActiveRole('client')}
              className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                activeRole === 'client'
                  ? 'bg-white text-slate-900 shadow-xs'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <User className="w-3.5 h-3.5 text-pink-600" />
              <span>Client</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveRole('companion')}
              className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                activeRole === 'companion'
                  ? 'bg-white text-pink-600 shadow-xs'
                  : 'text-slate-500 hover:text-slate-900'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5 text-pink-600" />
              <span>Companion Host</span>
            </button>
          </div>

          {/* Feedback Banners */}
          {errorMsg && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-xs font-bold text-rose-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
              <span>{errorMsg}</span>
            </div>
          )}

          {successMsg && (
            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs font-bold text-emerald-800 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
              <span>{successMsg}</span>
            </div>
          )}

          {/* Method Tabs: Mobile + Password vs Mobile OTP */}
          <div className="flex border-b border-slate-100 text-xs font-bold">
            <button
              type="button"
              onClick={() => {
                setLoginMethod('password');
                setForgotPasswordMode(false);
              }}
              className={`pb-2.5 px-4 cursor-pointer border-b-2 transition-all ${
                loginMethod === 'password'
                  ? 'border-pink-600 text-pink-600'
                  : 'border-transparent text-slate-400 hover:text-slate-700'
              }`}
            >
              Mobile & Password
            </button>
            <button
              type="button"
              onClick={() => {
                setLoginMethod('otp');
                setForgotPasswordMode(false);
              }}
              className={`pb-2.5 px-4 cursor-pointer border-b-2 transition-all ${
                loginMethod === 'otp'
                  ? 'border-pink-600 text-pink-600'
                  : 'border-transparent text-slate-400 hover:text-slate-700'
              }`}
            >
              Mobile OTP
            </button>
          </div>

          {/* Forgot Password via APITXT SMS OTP Flow */}
          {forgotPasswordMode ? (
            <div className="space-y-3.5 p-4 rounded-2xl bg-slate-50 border border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-extrabold uppercase tracking-wider text-slate-800">
                  Reset Password via Mobile OTP
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    setForgotPasswordMode(false);
                    setIsResetOtpSent(false);
                    setErrorMsg(null);
                  }}
                  className="text-[11px] font-bold text-pink-600 hover:text-pink-700 cursor-pointer"
                >
                  Back to Login
                </button>
              </div>

              {!isResetOtpSent ? (
                <form onSubmit={handleSendResetOtp} className="space-y-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-800 mb-1">
                      Registered 10-Digit Mobile Number
                    </label>
                    <div className="relative flex items-center">
                      <span className="absolute left-3 text-xs font-bold text-slate-500 font-mono select-none">
                        +91
                      </span>
                      <input
                        type="tel"
                        inputMode="numeric"
                        enterKeyHint="send"
                        required
                        maxLength={10}
                        placeholder="9876543210"
                        value={resetMobile}
                        onChange={(e) => setResetMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        className="w-full pl-12 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-mono font-bold text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-pink-500"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-2.5 bg-pink-600 hover:bg-pink-700 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-xs transition-all cursor-pointer disabled:opacity-60"
                  >
                    {isLoading ? 'Sending OTP...' : 'Send Reset OTP via SMS'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleConfirmPasswordReset} className="space-y-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-800 mb-1">
                      6-Digit SMS OTP Code
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      required
                      placeholder="Enter 6-digit OTP"
                      value={resetOtp}
                      onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-center font-mono font-bold text-sm tracking-widest text-slate-900"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-800 mb-1">
                      New Password (min 8 chars, letters + numbers)
                    </label>
                    <input
                      type="password"
                      required
                      minLength={8}
                      placeholder="New strong password"
                      value={resetNewPassword}
                      onChange={(e) => setResetNewPassword(e.target.value)}
                      className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-900"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-800 mb-1">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      required
                      minLength={8}
                      placeholder="Confirm new password"
                      value={resetConfirmPassword}
                      onChange={(e) => setResetConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-900"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-xs transition-all cursor-pointer disabled:opacity-60"
                  >
                    {isLoading ? 'Updating Password...' : 'Verify OTP & Set New Password'}
                  </button>
                </form>
              )}
            </div>
          ) : loginMethod === 'password' ? (
            <form onSubmit={handlePhonePasswordLogin} className="space-y-3.5">
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">
                  10-Digit Mobile Number
                </label>
                <div className="relative flex items-center">
                  <span className="absolute left-3 text-xs font-bold text-slate-500 font-mono select-none">
                    +91
                  </span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    enterKeyHint="next"
                    autoComplete="tel"
                    required
                    maxLength={10}
                    placeholder="9876543210"
                    value={loginMobile}
                    onChange={(e) => setLoginMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    className="w-full pl-12 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono font-bold text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white"
                  />
                  <Smartphone className="w-4 h-4 text-slate-400 absolute right-3 pointer-events-none" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-bold text-slate-800">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setForgotPasswordMode(true);
                      setErrorMsg(null);
                      setSuccessMsg(null);
                    }}
                    className="text-[11px] font-bold text-pink-600 hover:text-pink-700 cursor-pointer"
                  >
                    Forgot Password?
                  </button>
                </div>
                <div className="relative flex items-center">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    enterKeyHint="done"
                    autoComplete="current-password"
                    required
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-9 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white"
                  />
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none" />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 text-slate-400 hover:text-slate-700 cursor-pointer"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
              >
                {isLoading ? (
                  <span>Signing in...</span>
                ) : (
                  <>
                    <span>Log In to Account</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          ) : (
            /* Mobile Flow */
            <div className="space-y-3.5">
              {!isOtpSent ? (
                <form onSubmit={handleSendMobileOtp} className="space-y-3.5">
                  <div>
                    <label className="block text-xs font-bold text-slate-800 mb-1">
                      Mobile Number
                    </label>
                    <div className="relative flex items-center">
                      <span className="absolute left-3 text-xs font-bold text-slate-500 font-mono select-none">
                        +91
                      </span>
                      <input
                        type="tel"
                        required
                        maxLength={10}
                        placeholder="9876543210"
                        value={mobileNumber}
                        onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, ''))}
                        className="w-full pl-12 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono font-bold text-slate-900 placeholder:text-slate-400 focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:bg-white"
                      />
                      <Smartphone className="w-4 h-4 text-slate-400 absolute right-3 pointer-events-none" />
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 bg-pink-600 hover:bg-pink-700 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-md transition-all cursor-pointer"
                  >
                    Send Verification Code
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyMobileOtp} className="space-y-3.5">
                  <div>
                    <label className="block text-xs font-bold text-slate-800 mb-1">
                      Enter 6-Digit OTP Code
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="6-digit OTP"
                      value={mobileOtp}
                      onChange={(e) => setMobileOtp(e.target.value)}
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-center font-mono font-bold text-sm tracking-widest text-slate-900"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-md transition-all cursor-pointer"
                  >
                    Verify & Access Account
                  </button>
                </form>
              )}
            </div>
          )}

          {/* Sign Up Link */}
          <div className="pt-2 border-t border-slate-100 text-center space-y-2">
            <p className="text-xs text-slate-500">
              Don't have a Client account yet?
            </p>
            <Link
              to="/signup"
              className="w-full py-2.5 px-4 bg-pink-50 hover:bg-pink-100 text-pink-700 font-bold text-xs rounded-xl border border-pink-200 transition-all flex items-center justify-center gap-1.5 cursor-pointer block"
            >
              <span>Create Client Account (Free)</span>
            </Link>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center text-xs text-slate-400 border-t border-slate-200">
        PairUpToday Secure Member Authentication • 100% Google-Free Architecture
      </footer>
    </div>
  );
};

export default ClientLoginPage;
